import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Users, Bell, UserCheck } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTeamsStore } from '../store/useTeamsStore'
import { teamsService } from '../services/teamsService'
import { TeamList } from '../components/teams/TeamList'
import { CreateTeamCard } from '../components/teams/CreateTeamCard'
import { CreateTeamModal } from '../components/teams/CreateTeamModal'
import { FILL_GRADIENT, LINE_GRADIENT } from '../constants/gradients'
import { analyticsService } from '../services/analyticsService'

const TeamsPage = () => {
  const navigate = useNavigate()
  const {
    teams,
    invitations,
    loading,
    error,
    currentTab,
    setCurrentTab,
    refreshTeams,
    refreshInvitations,
    clearError
  } = useTeamsStore()

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null)

  useEffect(() => {
    // Load initial data
    refreshTeams()
    refreshInvitations()
  }, [])

  const handleTabChange = (tab: 'my-teams' | 'invitations') => {
    setCurrentTab(tab)
    clearError()
    
    // Track tab change
    analyticsService.track({
      name: 'team_tab_changed',
      properties: {
        tab
      }
    });
  }

  const handleCreateTeam = async (teamData: any) => {
    try {
      await teamsService.createTeam(teamData)
      
      // Track team creation
      analyticsService.track({
        name: 'team_created',
        properties: {
          team_name: teamData.name,
          has_description: !!teamData.description
        }
      });
      
      await refreshTeams()
      toast.success('Team created successfully')
      setShowCreateModal(false)
    } catch (error: any) {
      console.error('Failed to create team:', error)
      const message = error.response?.data?.message || 'Failed to create team'
      
      // Show specific error messages for common cases
      if (message.includes('Insufficient permissions')) {
        toast.error('You don\'t have permission to create teams. Ask an admin to grant team:create permission.')
      } else if (message.includes('already exists')) {
        toast.error('A team with this name or slug already exists')
      } else {
        toast.error(message)
      }
    }
  }

  const handleJoinTeam = async (teamId: string) => {
    try {
      await teamsService.joinTeam(teamId)
      await refreshTeams()
      await refreshInvitations()
      toast.success('Successfully joined the team')
    } catch (error) {
      console.error('Failed to join team:', error)
      toast.error('Failed to join team')
    }
  }

  const handleDeclineInvitation = async (invitationId: string) => {
    try {
      await teamsService.declineInvitation(invitationId)
      await refreshInvitations()
      toast.success('Invitation declined')
    } catch (error) {
      console.error('Failed to decline invitation:', error)
      toast.error('Failed to decline invitation')
    }
  }

  const renderTabContent = () => {
    switch (currentTab) {
      case 'my-teams':
        return (
          <div className="space-y-6">
            {/* My Teams Section */}
            <div className="space-y-4">
              {teams.length === 0 ? (
                <CreateTeamCard onCreateTeam={() => setShowCreateModal(true)} />
              ) : (
                <TeamList
                  teams={teams}
                  loading={loading.teams}
                  onInviteMembers={(teamId) => {
                    setSelectedTeam(teamId)
                    setShowInviteModal(true)
                  }}
                  onTeamAction={(teamId, action) => {
                    if (action === 'view') {
                      navigate(`/teams/${teamId}`)
                    } else {
                      // Handle other team actions (edit, archive, etc.)
                      console.log(`Team ${teamId} action: ${action}`)
                    }
                  }}
                />
              )}
            </div>
          </div>
        )
      
      case 'invitations':
        return (
          <div className="space-y-6">
            {invitations.length === 0 ? (
              <div className="text-center py-16">
                <Bell className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-2">
                  No Invitations
                </h3>
                <p className="text-gray-500 dark:text-dark-text-muted">
                  You don't have any pending team invitations.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {invitations.map((invitation) => (
                  <div
                    key={invitation.id}
                    className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl p-6 flex items-center justify-between hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center space-x-4">
                      <div 
                        className="w-14 h-14 rounded-full flex items-center justify-center"
                        style={{ background: 'linear-gradient(135deg, #FCB902 0%, #FF00CF 100%)' }}
                      >
                        <Users className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900 dark:text-dark-text">
                          {invitation.team.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-0.5">
                          Invited by {invitation.invitedBy.name} • <span className="capitalize">{invitation.role}</span>
                        </p>
                        <p className="text-xs text-gray-400 dark:text-dark-text-muted mt-1">
                          {new Date(invitation.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleDeclineInvitation(invitation.id)}
                        className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
                      >
                        Decline
                      </button>
                      <button
                        onClick={() => handleJoinTeam(invitation.team.id)}
                        className="px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95 flex items-center gap-2"
                        style={{ background: FILL_GRADIENT }}
                      >
                        <UserCheck className="w-4 h-4" />
                        Accept
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      <div className="max-w-7xl mx-auto  sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-900 dark:text-dark-text">
                Your Teams
              </h1>
              <p className="text-gray-500 dark:text-dark-text-muted mt-1">
                Collaborate with your team on games
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95 flex items-center gap-2"
              style={{ background: FILL_GRADIENT }}
            >
              <Plus className="w-5 h-5" />
              Create Team
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <nav className="flex gap-8 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => handleTabChange('my-teams')}
              className={`py-2 px-1 font-semibold text-sm transition-all duration-200 relative ${
                currentTab === 'my-teams'
                  ? 'text-gray-900 dark:text-dark-text'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              My Teams
              {currentTab === 'my-teams' && (
                <div 
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ background: LINE_GRADIENT }}
                />
              )}
            </button>
            <button
              onClick={() => handleTabChange('invitations')}
              className={`py-2 px-1 font-semibold text-sm transition-all duration-200 relative ${
                currentTab === 'invitations'
                  ? 'text-gray-900 dark:text-dark-text'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Invitations
              {currentTab === 'invitations' && (
                <div 
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ background: LINE_GRADIENT }}
                />
              )}
              {invitations.length > 0 && (
                <span className="absolute -top-1 -right-2 w-2 h-2 rounded-full" style={{ background: '#FF00CF' }}></span>
              )}
            </button>
          </nav>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Tab Content */}
        {renderTabContent()}

        {/* Create Team Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center lg:pl-64">
            <CreateTeamModal
              onClose={() => setShowCreateModal(false)}
              onCreateTeam={handleCreateTeam}
            />
          </div>
        )}

        {/* Invite Members Modal */}
        {showInviteModal && selectedTeam && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 lg:pl-64">
            <div className="bg-white dark:bg-dark-surface rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-dark-text">
                Invite Members
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-2">
                    Email addresses (comma separated)
                  </label>
                  <textarea
                    placeholder="john@example.com, jane@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-dark-bg dark:border-dark-border dark:text-dark-text"
                    rows={3}
                  />
                </div>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => setShowInviteModal(false)}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // Handle invite logic
                      setShowInviteModal(false)
                    }}
                    className="btn btn-primary"
                  >
                    Send Invitations
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TeamsPage 